import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useProfileStore } from '@/stores/profileStore'
import { useEffect } from 'react'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireOnboarding?: boolean
}

export function ProtectedRoute({ children, requireOnboarding = true }: ProtectedRouteProps) {
  const location = useLocation()
  const { user, isLoading: authLoading, isInitialized } = useAuthStore()
  const { profile, isLoading: profileLoading, fetchProfile } = useProfileStore()

  // Fetch profile when user is authenticated
  useEffect(() => {
    if (user && !profile && !profileLoading) {
      fetchProfile(user.id)
    }
  }, [user, profile, profileLoading, fetchProfile])

  // Show loading spinner while checking auth state
  if (!isInitialized || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF5A]" />
      </div>
    )
  }

  // Redirect to sign-in if not authenticated
  if (!user) {
    return <Navigate to="/auth/sign-in" state={{ from: location }} replace />
  }

  // Wait for the *initial* profile load. Re-fetches after the profile is
  // already loaded must NOT blank the UI — otherwise mid-flow re-fetches
  // (e.g. after onboarding submit) unmount the child page and reset its
  // local state, which manifests as "form jumped back to step 1 with no
  // completion modal".
  if (!profile && profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF5A]" />
      </div>
    )
  }

  // Check if onboarding is required and not complete. Role-aware target:
  // support persons (DB role 'supporter') get the dedicated wizard.
  if (requireOnboarding && profile && !profile.onboarding_completed) {
    if (!location.pathname.startsWith('/onboarding')) {
      const target = profile.role === 'supporter'
        ? '/onboarding/support-person'
        : '/onboarding'
      return <Navigate to={target} replace />
    }
  }

  return <>{children}</>
}
